import DashboardBlockHelper from './helpers/DashboardBlockHelper';
import ResizableForm from '../../mixins/ResizableForm.js';

/**
 * Dashboard-specific settings form wrapper for widget settings.
 */
Vue.asyncComponent('cms-dashboard-settings-form', {
    mixins: [ResizableForm],
    props: {
        dataProvider: {
            type: Object,
            required: true,
        },
    },
    /**
     * Create the reactive state for this dashboard component.
     */
    data() {
        return {
            showConfirm: false,
            confirmCallback: null,
            alertUnsaved: null,
            unsavedChangesMessage: false,
            loading: false,
            formData: false,
            blockToEdit: null,
            resolveBlockTimer: null,
            savingAndClosing: false,
        };
    },
    watch: {
        '$route.fullPath': {
            immediate: true,
            /**
             * Handle route changes that should reload the settings form.
             */
            handler() {
                this.formData = false;
                this.resolveBlockToEdit();
            },
        },
    },
    computed: {
        /**
         * Return whether the current widget actually exposes a settings form.
         */
        showForm() {
            return this.blockToEdit?.settings?.hasForm;
        },
        /**
         * Resolve the widget type from the block or route params.
         */
        settingsTypeParam() {
            return this.blockToEdit?.type
                || this.normalizeRouteParam(this.$route.params.widgetType)
                || this.normalizeRouteParam(this.$route.params.blockType)
                || null;
        },
        /**
         * Resolve the current widget reference id.
         */
        resolvedReferenceId() {
            return this.blockToEdit?.referenceId
                || this.normalizeRouteParam(this.$route.params.referenceId)
                || '';
        },
        /**
         * Resolve the persisted widget hash id used by the backend form endpoint.
         */
        resolvedHashId() {
            return this.blockToEdit?.id
                || this.normalizeRouteParam(this.$route.params.hashId)
                || this.normalizeRouteParam(this.$route.params.referenceId)
                || '';
        },
        /**
         * Build the settings form fetch url for the active widget.
         */
        formUrl() {
            return this.dataProvider.getSettingsFormUrl(
                this.settingsTypeParam,
                this.$route.query.verticalId,
                this.$route.params.blueprintId,
                this.resolvedHashId,
                this.$route.query.versionId,
            );
        },
        /**
         * Build the settings form save url for the active widget.
         */
        saveFormUrl() {
            return this.dataProvider.getSaveSettingsFormUrl(
                this.settingsTypeParam,
                this.$route.query.verticalId,
                this.$route.params.blueprintId,
                this.resolvedHashId,
                this.$route.query.versionId,
            );
        },
        /**
         * Forward the relation string used by nested form controls.
         */
        relationString() {
            return this.$route.params.relationString;
        },
        /**
         * Reuse the standard editor width persistence key.
         */
        localStorageWidthId() {
            return 'editor-settings-width';
        },
    },
    methods: {
        /**
         * Normalize route params to nullable strings.
         */
        normalizeRouteParam(value) {
            if (value === null || typeof value === 'undefined') {
                return null;
            }

            const normalizedValue = String(value).trim();
            return normalizedValue === '' || normalizedValue === 'undefined'
                ? null
                : normalizedValue;
        },
        /**
         * Persist settings in the parent editor state and close the panel.
         */
        saveSettings($event) {
            this.savingAndClosing = true;
            this.alertUnsaved = false;
            this.unsavedChangesMessage = false;

            const settingsForm = this.$refs.settingsForm;
            if (settingsForm?.formValues) {
                settingsForm.initialFormValues = JSON.parse(JSON.stringify(settingsForm.formValues));
            }

            this.$emit('saveSettings', { ...$event, referenceId: this.resolvedReferenceId });

            this.$nextTick(() => {
                this.close();
                this.savingAndClosing = false;
            });
        },
        /**
         * Forward widget renames to the parent editor.
         */
        updateName($event) {
            this.$emit('blockPropertyChanged', { ...$event, referenceId: this.resolvedReferenceId });
        },
        /**
         * Execute the confirmed close callback.
         */
        confirmDialog() {
            this.showConfirm = false;
            this.confirmCallback();
        },
        /**
         * Update the unsaved-changes state coming from the form.
         */
        alertUnsavedChanges({ showAlert, message }) {
            this.alertUnsaved = showAlert;
            this.unsavedChangesMessage = message;
        },
        /**
         * Guard closing when the form has unsaved changes.
         */
        attemptClose(close) {
            if (this.savingAndClosing || !this.alertUnsaved) {
                close();
                return;
            }

            this.showConfirm = true;
            this.confirmCallback = close;
        },
        /**
         * Close the settings view by returning to the parent route.
         */
        close() {
            this.$router.push({
                ...this.$route.matched[0],
                query: this.$route.query,
            });
        },
        /**
         * Resolve a block by persisted id as a fallback for malformed routes.
         */
        getBlock(blocks, id) {
            if (!Array.isArray(blocks) || !id) {
                return null;
            }

            for (const block of blocks) {
                if (block.id === id) {
                    return block;
                }

                const childBlocks = Array.isArray(block.blocks) ? block.blocks : [];
                const childResult = this.getBlock(childBlocks, id);

                if (childResult) {
                    return childResult;
                }
            }

            return null;
        },
        /**
         * The settings route can point to either a saved hash id or a transient
         * reference id, so collect both candidates in one place.
         */
        getRouteBlockIdentifiers() {
            return {
                hashId: this.normalizeRouteParam(this.$route.params.hashId),
                referenceId: this.normalizeRouteParam(this.$route.params.referenceId),
            };
        },
        /**
         * Try the route identifiers in the same order the settings flow needs:
         * first the runtime reference id, then persisted ids as fallback.
         */
        findBlockToEdit(blocks) {
            const { hashId, referenceId } = this.getRouteBlockIdentifiers();
            const directReferenceMatch = DashboardBlockHelper.getBlock(blocks, referenceId);

            if (directReferenceMatch) {
                return directReferenceMatch;
            }

            const persistedHashMatch = this.getBlock(blocks, hashId);

            if (persistedHashMatch) {
                return persistedHashMatch;
            }

            return this.getBlock(blocks, referenceId);
        },
        /**
         * The settings panel can open before the parent editor has finished
         * hydrating its blocks, so retry a few times before giving up.
         */
        scheduleResolveBlockRetry(attempt) {
            this.resolveBlockTimer = setTimeout(() => {
                this.resolveBlockToEdit(attempt + 1);
            }, 50);
        },
        /**
         * Resolve the widget currently edited by the settings route.
         */
        resolveBlockToEdit(attempt = 0) {
            clearTimeout(this.resolveBlockTimer);

            const blocks = this.dataProvider?.getBlocks?.() || [];
            const blockToEdit = this.findBlockToEdit(blocks);

            this.blockToEdit = blockToEdit;

            if (this.showForm) {
                this.getFormData();
                return;
            }

            if (!blockToEdit && attempt < 10) {
                this.scheduleResolveBlockRetry(attempt);
            }
        },
        /**
         * Fetch the AppKit form payload for the resolved widget.
         */
        async getFormData() {
            const params = {
                locale: this.$localeHandler.getActiveLocale(),
            };
            this.loading = true;

            try {
                const data = {
                    formValues: this.blockToEdit.settings?.values,
                };
                this.formData = await this.$post(this.formUrl, data, params);
                this.loading = false;
            } catch (error) {
                this.loading = false;
            }
        },
    },
    /**
     * Clean up dashboard behavior before the component is destroyed.
     */
    beforeDestroy() {
        clearTimeout(this.resolveBlockTimer);
    },
}, 'cms/dashboard/editor/cms-dashboard-settings-form.html');
